import Button from '@/components/Button';
import { ButtonProps } from '@/components/Button/Button';
import ChoiceButton from '@/components/ChoiceButton';
import Input from '@/components/Input';
import TableSelect from '@/components/TableSelect';
import Tooltip from '@/components/Tooltip';
import ArrowLeftCircle from '@/components/icons/ArrowLeftCircle';
import useNavigate from '@/hooks/useNavigate';
import { AdminFormModel } from '@/services/API';
import { useAppSelector } from '@/store/hooks';
import form from '@/styles/form.module.css';
import { AccountStatus, ISelectNumeroAgrement } from '@/types';
import { getEmailError, getEmptyError } from '@/types/Input';
import { getNumberOfDaysSince } from '@/utils/dates';
import { default as env, default as getEnv } from '@/utils/env';
import {
  isNumeric,
  isValidDate,
  isValidEmail,
  isValidPhoneNumber,
} from '@/utils/validators';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import styles from './styles.module.scss';

export default function Form({
  admin,
  primaryBtnProps,
  title,
  pdvs,
  uid,
  lastLoginTime,
  setAdmin,
  setPdvs,
}: {
  admin: AdminFormModel;
  primaryBtnProps?: ButtonProps;
  title: string;
  pdvs: ISelectNumeroAgrement[];
  uid?: string;
  lastLoginTime?: string;
  setAdmin: Dispatch<SetStateAction<AdminFormModel | undefined>>;
  setPdvs: Dispatch<SetStateAction<ISelectNumeroAgrement[]>>;
}) {
  const navigate = useNavigate();
  const [mfaDisable, setMfaDisable] = useState(true);
  const selectedSte = useAppSelector((state) => state.selectedSte.societe);
  const profil = env().PROFIL;
  const ERROR_MESSAGE_MFA =
    "L'adresse e-mail ou le numéro de mobile doit être renseigné pour la double authentification";

  const [valids, setValids] = useState({
    given_name: true,
    sn: true,
    email_address: true,
    mobile: true,
    federation_id: true,
  });

  const [errors, setErrors] = useState({
    given_name: '',
    sn: '',
    email_address: '',
    mobile: '',
    federation_id: '',
  });

  const authn_method = useAppSelector(
    (state) => state.selectedSte.societe.authn_method,
  );

  const emailValidator = (value: string) => {
    return value === undefined || value === '' || isValidEmail(value);
  };

  const nameValidator = (value: string) => {
    return value !== '';
  };

  const mobileValidator = (value: string) => {
    return value === undefined || value === '' || isValidPhoneNumber(value);
  };
  const federateValidator = (value: string) => {
    return (
      selectedSte.authn_method === 'LOCAL' ||
      (value !== undefined && value !== '')
    );
  };

  const isMFAModifiable = () => {
    return profil !== 'PARTENAIRE' || !admin.mfa_capable;
  };

  useEffect(() => {
    if (admin) {
      setValids({
        given_name: nameValidator(admin.given_name),
        sn: nameValidator(admin.sn),
        email_address: emailValidator(admin.email_address),
        mobile: mobileValidator(admin.mobile),
        federation_id: federateValidator(admin.federation_id),
      });
    }
  }, []);

  useEffect(() => {
    if (!admin || !isMFAModifiable()) {
      return;
    }
    if (
      (admin.email_address === '' || !isValidEmail(admin.email_address)) &&
      (admin.mobile === '' || !isNumeric(admin.mobile))
    ) {
      setMfaDisable(true);
      setAdmin({ ...admin, mfa_capable: false });
    } else setMfaDisable(false);
  }, [admin.email_address, admin.mobile]);

  return (
    <div className={`flex-column gap-l ${form.content}`}>
      <Button
        variant="ghost"
        icon={<ArrowLeftCircle />}
        onClick={() => navigate(-1)}
      >
        Retour
      </Button>

      <form className={`${form.form} gap-l `}>
        <h3 className={form.title}>{title}</h3>

        <Input
          label="Prénom *"
          placeholder="Pierre"
          value={admin.given_name}
          maxLength={50}
          onChange={(e) => {
            setAdmin({
              ...admin,
              given_name: e.target.value,
            });
            setErrors({ ...errors, given_name: '' });
          }}
          onBlur={(e) => {
            const valid = nameValidator(e.target.value);
            if (!valid) {
              const errorMessage = getEmptyError(e.target.value);
              setErrors({ ...errors, given_name: errorMessage });
              setValids({ ...valids, given_name: false });
            } else setValids({ ...valids, given_name: true });
          }}
          errorMessage={errors.given_name}
        />

        <Input
          label="Nom *"
          placeholder="Bertrand"
          value={admin.sn}
          maxLength={50}
          onChange={(e) => {
            setAdmin({
              ...admin,
              sn: e.target.value,
            });
            setErrors({ ...errors, sn: '' });
          }}
          onBlur={(e) => {
            const valid = nameValidator(e.target.value);
            if (!valid) {
              const errorMessage = getEmptyError(e.target.value);
              setErrors({ ...errors, sn: errorMessage });
              setValids({ ...valids, sn: false });
            } else setValids({ ...valids, sn: true });
          }}
          errorMessage={errors.sn}
        />

        {uid && <Input label="Identifiant de connexion" value={uid} disabled />}

        <Input
          label="Adresse e-mail"
          placeholder="pierre.bertrand@hotmail.fr"
          value={admin.email_address ?? ''}
          onChange={(e) => {
            setAdmin({
              ...admin,
              email_address: e.target.value,
            });
            setErrors({ ...errors, email_address: '' });
          }}
          onBlur={(e) => {
            const valid = emailValidator(e.target.value);
            const errorMessage = getEmailError(e.target.value);
            if (!valid) {
              setErrors({ ...errors, email_address: errorMessage });
              setValids({ ...valids, email_address: false });
            } else if (
              !isMFAModifiable() &&
              (e.target.value ?? '').length < 1 &&
              (!mobileValidator(admin.mobile ?? '') ||
                (admin.mobile ?? '').length < 1)
            ) {
              setErrors({
                ...errors,
                email_address: ERROR_MESSAGE_MFA,
                mobile: ERROR_MESSAGE_MFA,
              });
              setValids({ ...valids, email_address: false, mobile: false });
            } else if (
              mobileValidator(admin.mobile ?? '') ||
              (admin.mobile ?? '').length === 0
            ) {
              setValids({ ...valids, mobile: true, email_address: true });
              setErrors({ ...errors, email_address: '', mobile: '' });
            } else {
              setValids({ ...valids, email_address: true });
            }
          }}
          errorMessage={errors.email_address}
          helperText="L'adresse e-mail doit être unique dans votre annuaire car elle peut servir d'identifiant de connexion."
        />

        <Input
          label="Numéro de mobile"
          placeholder="+33700000000"
          value={admin.mobile ?? ''}
          onChange={(e) => {
            if (isNumeric(e.target.value)) {
              setAdmin({
                ...admin,
                mobile: e.target.value,
              });
              setErrors({ ...errors, mobile: '' });
            }
          }}
          onBlur={(e) => {
            const valid = mobileValidator(e.target.value);
            const errorMessage =
              'Veuillez entrer un numéro de téléphone valide';
            if (!valid) {
              setErrors({ ...errors, mobile: errorMessage });
              setValids({ ...valids, mobile: false });
            } else if (
              !isMFAModifiable() &&
              (e.target.value ?? '').length < 1 &&
              (!emailValidator(admin.email_address ?? '') ||
                (admin.email_address ?? '').length < 1)
            ) {
              setErrors({
                ...errors,
                mobile: ERROR_MESSAGE_MFA,
                email_address: ERROR_MESSAGE_MFA,
              });
              setValids({ ...valids, mobile: false, email_address: false });
            } else {
              if (
                emailValidator(admin.email_address ?? '') ||
                (admin.email_address ?? '').length === 0
              ) {
                setValids({ ...valids, mobile: true, email_address: true });
                setErrors({ ...errors, email_address: '', mobile: '' });
              } else setValids({ ...valids, mobile: true });
            }
          }}
          errorMessage={errors.mobile}
          maxLength={50}
          helperText="Veuillez entrer un numéro de téléphone au format international (ex : +33764263256) sans espacement
          Le numéro de mobile doit être unique dans votre annuaire."
        />

        {admin.account_status && (
          <div className="flex-column gap-s">
            <p>Status</p>
            <div className={`flex-row gap-m ${styles.choices}`}>
              <ChoiceButton
                type="button"
                onClick={() => {
                  setAdmin({
                    ...admin,
                    account_status: AccountStatus.ACTIVE,
                  });
                }}
                selected={admin.account_status === AccountStatus.ACTIVE}
                disabled={
                  profil === 'PF' &&
                  admin.account_status === AccountStatus.DISABLED_PARTNER
                }
              >
                Actif
              </ChoiceButton>
              <ChoiceButton
                type="button"
                disabled={
                  profil === 'PF' &&
                  admin.account_status === AccountStatus.DISABLED_PARTNER
                }
                onClick={() => {
                  setAdmin({
                    ...admin,
                    account_status:
                      getEnv().PROFIL === 'PF'
                        ? AccountStatus.DISABLED_BNPPF
                        : AccountStatus.DISABLED_PARTNER,
                  });
                }}
                selected={admin.account_status !== AccountStatus.ACTIVE}
              >
                {(() => {
                  switch (admin.account_status) {
                    case AccountStatus.DISABLED_BNPPF:
                      return 'Bloqué';
                    case AccountStatus.DISABLED_PARTNER:
                      return 'Désactivé';
                    default:
                      // 'ACTIVE' case
                      return env().PROFIL === 'PF' ? 'Bloqué' : 'Désactivé';
                  }
                })()}
              </ChoiceButton>
            </div>
          </div>
        )}
        {selectedSte.authn_method !== 'LOCAL' && (
          <div>
            <Input
              label="Identifiant de fédération *"
              placeholder="pierre.bertrand@hotmail.fr"
              value={admin.federation_id ?? ''}
              maxLength={50}
              onChange={(e) => {
                setAdmin({
                  ...admin,
                  federation_id: e.target.value,
                });
                setErrors({ ...errors, federation_id: '' });
              }}
              onBlur={(e) => {
                const valid = federateValidator(e.target.value);
                if (!valid) {
                  const errorMessage = getEmptyError(e.target.value);
                  setErrors({ ...errors, federation_id: errorMessage });
                  setValids({ ...valids, federation_id: false });
                } else setValids({ ...valids, federation_id: true });
              }}
              errorMessage={errors.federation_id}
              helperText="L'identifiant de fédération doit être unique dans votre annuaire."
            />
            <p className="s">
              Une fédération d&apos;identité est mise en place entre votre
              gestionnaire d&apos;identité (ex. OKTA) et le nôtre (PING). Pour
              se connecter à nos outils votre employé utilisera ses identifiant
              et mdp donnés par votre gestionnaire d&apos;identité. Renseignez
              ici l&apos;identifiant de votre employé dans votre gestionnaire
              d&apos;identité.
            </p>
          </div>
        )}

        {profil === 'PARTENAIRE' && (
          <div className="flex-column gap-s">
            <p>Type d’administrateur</p>
            <div className={`flex-row gap-m ${styles.choices}`}>
              <ChoiceButton
                type="button"
                onClick={() =>
                  setAdmin({
                    ...admin,
                    administrator_type: 'PARTIAL',
                  })
                }
                selected={admin.administrator_type === 'PARTIAL'}
              >
                Délégué
              </ChoiceButton>
              <ChoiceButton
                type="button"
                onClick={() =>
                  setAdmin({
                    ...admin,
                    administrator_type: 'GLOBAL',
                  })
                }
                selected={admin.administrator_type === 'GLOBAL'}
              >
                Global
              </ChoiceButton>
            </div>
          </div>
        )}

        {authn_method !== 'FEDERATED' && (
          <div className="flex-column gap-s">
            <div className="flex-row gap-s">
              <p>Double authentification</p>
              <Tooltip
                message={`Lorsque la double authentification est activée, elle sera demandée pour accéder à nos applications.`}
              />
            </div>
            <div className={`flex-row gap-m ${styles.choices}`}>
              <ChoiceButton
                type="button"
                disabled={!isMFAModifiable() || mfaDisable}
                onClick={() => {
                  if (isMFAModifiable()) {
                    setAdmin({ ...admin, mfa_capable: true });
                  }
                }}
                selected={admin.mfa_capable}
              >
                Oui
              </ChoiceButton>
              <ChoiceButton
                type="button"
                onClick={() => {
                  if (isMFAModifiable()) {
                    setAdmin({ ...admin, mfa_capable: false });
                  }
                }}
                disabled={!isMFAModifiable()}
                selected={!admin.mfa_capable}
              >
                Non
              </ChoiceButton>
            </div>
          </div>
        )}

        {lastLoginTime && (
          <Input
            label="Date de dernière connexion"
            placeholder="-"
            value={
              isValidDate(lastLoginTime)
                ? `${new Date(lastLoginTime).toLocaleDateString()} (${
                    getNumberOfDaysSince(lastLoginTime) + ' Jours'
                  })`
                : '-'
            }
            disabled
          />
        )}
        {admin.administrator_type === 'PARTIAL' && profil === 'PARTENAIRE' && (
          <div className="flex-column gap-s">
            <p>
              Parmi les numéros d’agrément disponibles, sélectionnez ceux que
              l&apos;administrateur peut administrer.
            </p>
            <TableSelect pdvs={pdvs} setPdvs={setPdvs} />
          </div>
        )}
        <p className="xs">* Champs obligatoires</p>
        <div className={`${styles['validate-button']}`}>
          {primaryBtnProps && (
            <Button
              {...primaryBtnProps}
              disabled={Object.values(valids).some((valid) => !valid)}
              type="button"
            />
          )}
        </div>
      </form>
    </div>
  );
}
